import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PhoneCall, MessageCircle, ShieldCheck, Home, Sun, Thermometer, CheckCircle2 } from 'lucide-react';

const BRAND_GREEN = '#5EA74C';
const EYEBROW_GREEN = '#3F7A32';
const PHONE_TEL = '042212959978';
const WA = 'https://wa.me/4942212959978';
const HERO_IMG = '/media/team/DSC09798';

const TRUST = [
  'Dachdeckermeisterbetrieb aus Delmenhorst',
  'Dach, Photovoltaik und Wärmepumpe aus einer Hand',
  'Förderberatung inklusive',
  'Ein Ansprechpartner von der Planung bis zur Umsetzung',
];

const SERVICES = [
  { label: 'Dachsanierung', to: '/service/dachsanierung', icon: Home },
  { label: 'Photovoltaik', to: '/service/photovoltaik-montage', icon: Sun },
  { label: 'Wärmepumpe', to: '/waermepumpe', icon: Thermometer },
];

const HeroImage = ({ className, position, sizes }) => (
  <img
    src={`${HERO_IMG}-1600.webp`}
    srcSet={`${HERO_IMG}-800.webp 800w, ${HERO_IMG}-1200.webp 1200w, ${HERO_IMG}-1600.webp 1600w`}
    sizes={sizes}
    alt="Das Team von Green Lion Energy vor einem Wohnhaus in der Region Delmenhorst"
    className={className}
    style={{ objectPosition: position }}
    width="2000"
    height="1333"
    loading="eager"
    fetchPriority="high"
    decoding="async"
  />
);

const HeroPowerdecker = () => (
  <section className="relative bg-white" data-testid="hero-section">
    {/* Desktop */}
    <div className="hidden lg:block relative min-h-[760px] xl:min-h-[820px] -mt-[120px] overflow-hidden">
      <div className="absolute inset-y-0 right-0 w-[52%]">
        <HeroImage className="w-full h-full object-cover" position="80% 40%" sizes="52vw" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, #ffffff 0%, rgba(255,255,255,0.7) 10%, rgba(255,255,255,0) 26%)' }} />
        <div className="absolute inset-x-0 bottom-0 h-16" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, #ffffff 100%)' }} />
      </div>
      <div className="container mx-auto px-4 relative z-10 flex items-center min-h-[760px] xl:min-h-[820px] pt-[120px]">
        <div className="max-w-[600px] py-14">
          <p className="text-[13px] font-extrabold tracking-[0.22em] uppercase" style={{ color: EYEBROW_GREEN }} data-testid="hero-positionierung-desktop">
            Dach · Photovoltaik · Wärmepumpe
          </p>
          <h1 className="mt-4 text-5xl xl:text-[64px] font-black tracking-tight text-gray-900 leading-[1.02]">
            Dachsanierung mit Photovoltaik –
            <span className="block">wirklich aus einer Hand.</span>
          </h1>
          <p className="mt-6 text-lg xl:text-xl text-gray-700 leading-relaxed max-w-[560px]">
            Ein Ansprechpartner für die energetische Modernisierung Ihres Hauses – von der Dachsanierung
            über die eigene Stromerzeugung bis zur modernen Wärmepumpe.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/projekt-anfrage" className="gle-cta inline-flex items-center rounded-2xl px-8 py-4 font-bold text-white text-base" data-testid="hero-cta-desktop">
              Kostenlose Ersteinschätzung <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center rounded-2xl px-5 py-4 font-bold text-gray-900 bg-white border border-black/10 hover:border-black/25 transition-colors" data-testid="hero-phone-desktop">
              <PhoneCall className="w-5 h-5 mr-2" style={{ color: BRAND_GREEN }} /> 04221-2959978
            </a>
            <a href={WA} target="_blank" rel="noopener noreferrer" className="inline-flex items-center rounded-2xl px-4 py-4 font-bold text-gray-900 bg-white border border-black/10 hover:border-black/25 transition-colors" aria-label="WhatsApp" data-testid="hero-whatsapp-desktop">
              <MessageCircle className="w-5 h-5" style={{ color: BRAND_GREEN }} />
            </a>
          </div>
          <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-2.5">
            {TRUST.map((t) => (
              <li key={t} className="flex items-start gap-2 text-[15px] text-gray-800">
                <CheckCircle2 className="w-4.5 h-4.5 w-[18px] h-[18px] mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} /> {t}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex gap-2" data-testid="hero-service-chips-desktop">
            {SERVICES.map((s) => (
              <Link key={s.to} to={s.to} className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 text-white px-4 py-2 text-[13px] font-bold hover:bg-gray-800 transition-colors">
                <s.icon className="w-4 h-4" style={{ color: '#A3D692' }} /> {s.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Mobile / Tablet */}
    <div className="lg:hidden">
      <div className="relative aspect-square sm:aspect-[16/11] overflow-hidden">
        <HeroImage className="w-full h-full object-cover" position="42% 40%" sizes="100vw" />
        <div className="absolute inset-x-0 bottom-0 h-28" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, #ffffff 100%)' }} />
        <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-3 py-1.5 text-[11px] font-semibold text-gray-800">
          <ShieldCheck className="w-3.5 h-3.5" style={{ color: BRAND_GREEN }} /> Dachdeckermeisterbetrieb
        </div>
      </div>
      <div className="container mx-auto px-4 -mt-6 relative z-10 pb-2">
        <p className="text-[11px] font-extrabold tracking-[0.2em] uppercase" style={{ color: EYEBROW_GREEN }} data-testid="hero-positionierung-mobile">
          Dach · Photovoltaik · Wärmepumpe
        </p>
        <h1 className="mt-2 text-[28px] sm:text-4xl font-black tracking-tight text-gray-900 leading-[1.08]">
          Dachsanierung mit Photovoltaik – wirklich aus einer Hand.
        </h1>
        <p className="mt-3 text-[15px] sm:text-base text-gray-700 leading-relaxed">
          Ein Ansprechpartner für die energetische Modernisierung Ihres Hauses – von der Dachsanierung über die eigene Stromerzeugung bis zur modernen Wärmepumpe.
        </p>
        <Link to="/projekt-anfrage" className="gle-cta mt-5 inline-flex w-full items-center justify-center rounded-2xl px-6 py-4 font-bold text-white text-base" data-testid="hero-cta-mobile">
          Kostenlose Ersteinschätzung <ArrowRight className="w-5 h-5 ml-2" />
        </Link>
        <div className="mt-2.5 grid grid-cols-2 gap-2">
          <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center justify-center rounded-2xl px-4 py-3 font-bold text-gray-900 bg-white border border-black/10 text-sm">
            <PhoneCall className="w-4 h-4 mr-2" style={{ color: BRAND_GREEN }} /> Anrufen
          </a>
          <a href={WA} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-2xl px-4 py-3 font-bold text-gray-900 bg-white border border-black/10 text-sm">
            <MessageCircle className="w-4 h-4 mr-2" style={{ color: BRAND_GREEN }} /> WhatsApp
          </a>
        </div>
        <ul className="mt-5 space-y-2">
          {TRUST.slice(0, 3).map((t) => (
            <li key={t} className="flex items-start gap-2 text-sm text-gray-800">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} /> {t}
            </li>
          ))}
        </ul>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="hero-service-chips-mobile">
          {SERVICES.map((s) => (
            <Link key={s.to} to={s.to} className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 text-white px-3.5 py-1.5 text-[12px] font-bold">
              <s.icon className="w-3.5 h-3.5" style={{ color: '#A3D692' }} /> {s.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default HeroPowerdecker;
